import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '/@contexts/global-app-context';
import { useCreateCollectionMutation } from '/@services/collections/collection-service';
import {
    getUrlHash,
    replaceState,
} from '/@services/system/nav-service/nav-service';
import { CreateEditModal } from '../collections/create-edit-modal/create-edit-modal.container';
import { CreateLiveboardModal } from '../home/home-left-panel/create-liveboard-modal.container';

export const SPOTLIGHT_CREATE_LIVEBOARD_EVENT = 'spotlight:create-liveboard';
export const SPOTLIGHT_CREATE_COLLECTION_EVENT = 'spotlight:create-collection';

const CREATE_LIVEBOARD_ITEM_ID = 'action-create-liveboard';
const CREATE_COLLECTION_ITEM_ID = 'action-create-collection';

export const SpotlightCreateModalsContainer: React.FC = () => {
    const { navService } = useAppContext();
    const { search } = useLocation();
    const {
        createCollection,
        data: createCollectionData,
        loading: createCollectionLoading,
    } = useCreateCollectionMutation();

    // The openCreateModal trigger only matters at mount — it seeds the modal
    // state and the one-shot cleanup effect below. useLocation() is reactive,
    // so parsing it inline would recompute on every in-tab navigation for a
    // value that's never read again. Capture it once via a lazy initializer.
    const [createModalFromUrl] = useState(() =>
        new URLSearchParams(search).get('openCreateModal'),
    );

    const [showLiveboard, setShowLiveboard] = useState(
        createModalFromUrl === CREATE_LIVEBOARD_ITEM_ID,
    );
    const [showCollection, setShowCollection] = useState(
        createModalFromUrl === CREATE_COLLECTION_ITEM_ID,
    );

    const openLiveboardModal = useCallback(() => setShowLiveboard(true), []);
    const openCollectionModal = useCallback(() => setShowCollection(true), []);

    const didOpenModalFromUrl =
        createModalFromUrl === CREATE_LIVEBOARD_ITEM_ID ||
        createModalFromUrl === CREATE_COLLECTION_ITEM_ID;
    useEffect(() => {
        if (!didOpenModalFromUrl) return;
        const [hashPath, hashQuery = ''] = getUrlHash()
            .replace(/^#/, '')
            .split('?');
        const params = new URLSearchParams(hashQuery);
        params.delete('openCreateModal');
        const nextQuery = params.toString();
        const newHash = nextQuery
            ? `#${hashPath}?${nextQuery}`
            : `#${hashPath}`;
        replaceState(newHash);
    }, []);

    useEffect(() => {
        window.addEventListener(
            SPOTLIGHT_CREATE_LIVEBOARD_EVENT,
            openLiveboardModal,
        );
        window.addEventListener(
            SPOTLIGHT_CREATE_COLLECTION_EVENT,
            openCollectionModal,
        );
        return () => {
            window.removeEventListener(
                SPOTLIGHT_CREATE_LIVEBOARD_EVENT,
                openLiveboardModal,
            );
            window.removeEventListener(
                SPOTLIGHT_CREATE_COLLECTION_EVENT,
                openCollectionModal,
            );
        };
    }, [openLiveboardModal, openCollectionModal]);

    useEffect(() => {
        if (createCollectionData && createCollectionData.Status <= 300) {
            setShowCollection(false);
            navService.goToCollection(createCollectionData.Data.id);
        }
    }, [createCollectionData, navService]);

    return (
        <>
            <CreateLiveboardModal
                isModalOpen={showLiveboard}
                setIsModalOpen={setShowLiveboard}
                navService={navService as any}
            />
            {showCollection && (
                <CreateEditModal
                    isModalOpened={showCollection}
                    closeModal={() => setShowCollection(false)}
                    setShowError={Function.prototype as any}
                    loader={createCollectionLoading}
                    onSubmit={formData => createCollection(formData)}
                />
            )}
        </>
    );
};
